import { convertObjectFromOldPositionToNewPosition } from '@/commons/fabricjs/FabricPositionHelper';
import { FONT_SIZE_MULTIPLIER } from '@/littleWedding/projectConstantes';
import { Canvas, Group, FabricText, Point, type TPointerEventInfo } from 'fabric';
import type { IFabricWorldInitParams } from '@/commons/fabricjs/IFabricWorldInitParams';
import type { IFabricImageCustom } from '@/typescript/interfaces/IFabricImageCustom';
import type { IFabricTextCustom } from '@/typescript/interfaces/IFabricTextCustom';

class FabricWorld {
    fabricCanvas: Canvas | null;
    text: FabricText | null;
    canvasWidth: number;
    canvasHeight: number;
    imageMargin: { bottom?: number; left?: number; right?: number; top?: number } = {};
    fontSizeMultiplier: number = FONT_SIZE_MULTIPLIER;
    _clickRefPoint: Point | null = null;
    zoomCallback: (opt: TPointerEventInfo) => void;
    panCallback: (opt: TPointerEventInfo) => void;
    clickCallback: (opt: TPointerEventInfo) => void;

    constructor() {
        this.fabricCanvas = null;
    }

    init(params: IFabricWorldInitParams) {
        const { canvas, ...fabricOptions } = params;
        if (!canvas) {
            throw new Error('Canvas element is null');
        }
        // We default size to parent rendering size
        // sonarlint is wrong because we want 0 to go to default value
        this.canvasWidth = Math.floor(canvas.parentElement?.getBoundingClientRect().width ?? canvas.width);
        this.canvasHeight = Math.floor(canvas.parentElement?.getBoundingClientRect().height ?? canvas.height);
        canvas.width = this.canvasWidth;
        canvas.height = this.canvasHeight;
        this.fabricCanvas = new Canvas(canvas, fabricOptions);
    }

    displayRealSizeCanvas() {}

    /**
     * Usefull to set a reference point when user click on canvas outside FabricWorld instance
     * used by panCallback and clickCallback
     * @param point Point object or null
     */
    setClickedRefPoint = (point: Point | null) => {
        this._clickRefPoint = point;
    };

    /**
     * Usefull to set a reference point when user click on canvas outside FabricWorld instance
     * used by panCallback and clickCallback
     * @param {IFabricImageCustom | IFabricTextCustom} object
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setClickedActiveObject = (object?: IFabricImageCustom | IFabricTextCustom) => {};

    /**
     * Usefull to get a reference point when user click on canvas outside FabricWorld instance
     * used by panCallback and clickCallback
     * @returns Point object or null
     */
    getClickedRefPoint = () => {
        return this._clickRefPoint;
    };

    /**
     * Return a canvas element that you may use as a texture in a 3D object
     */
    getTexture(): HTMLCanvasElement | undefined {
        return this.fabricCanvas?.toCanvasElement();
    }

    serialize() {
        const jsonProject = this.fabricCanvas?.toDatalessJSON(['name']);
        jsonProject.percentUtils = {
            canvasW: this.canvasWidth,
            canvasH: this.canvasHeight,
        };
        return jsonProject;
    }

    unserialize(jsonProject: { percentUtils?: { canvasH: number; canvasW: number } }) {
        // this.fabricCanvas?.clear();
        this.fabricCanvas?.renderAll();
        this.fabricCanvas
            ?.loadFromJSON(jsonProject, (jsonObject, fObject) => {
                // @ts-expect-error in the future you might want to create a class that extend fabric text which include all you need
                fObject.name = jsonObject.name;
            })
            .then(() => {
                this.fabricCanvas?.getObjects().forEach((fObject) => {
                    // Some config are not saved, so we reapply them by type
                    if (fObject.type === 'group') {
                        const group = fObject as Group;
                        group.selectable = false;
                        group.evented = false;
                    } else if (['text', 'textbox'].includes(fObject.type)) {
                        // applyTextDefaultConfig(fObject as FabricText);
                        // this.fabricCanvas?.setActiveObject(fObject);
                    }

                    // convert position to new canvas size (fix retina or devicePixelRatio issues)
                    convertObjectFromOldPositionToNewPosition(
                        fObject,
                        jsonProject.percentUtils?.canvasW,
                        jsonProject.percentUtils?.canvasH,
                        this.canvasWidth,
                        this.canvasHeight,
                    );

                    // force update of object
                    fObject.setCoords(); // update handle positions
                    fObject.set('dirty', true);
                });
                this.fabricCanvas?.renderAll();
            });
    }

    dispose(): void {
        if (!this.fabricCanvas) {
            return;
        }
        // destroy event listeners here
        this.fabricCanvas.off('mouse:wheel', this.zoomCallback);
        this.fabricCanvas.off('mouse:down', this.clickCallback);
        this.fabricCanvas.off('mouse:move', this.panCallback);
        this.fabricCanvas.off('mouse:up', this.clickCallback);
        this.fabricCanvas.dispose();
    }
}

export { FabricWorld };
